import { gql, useQuery } from '@apollo/client';
import styled from '@emotion/styled';
import { languagesQuery, meWithTutorialQuery } from 'graphql/queries';
import { LanguagesQuery, Me, MeWithTutorialQuery } from 'graphql/types';
import i18n from 'i18n';
import i18next from 'i18next';
import colors from 'lib/colors';
import { useChangeUserLanguage } from 'lib/hooks';
import { useState } from 'react';
import DropDown from './DropDown';
import StyledArrow from './StyledArrow';

interface LanguageSwitchProps {
  right?: boolean;
}

const LanguageSwitch = ({ right }: LanguageSwitchProps) => {
  const [langSwitcherOpened, setLangSwitcherOpened] = useState(false);
  const toggleLanguageSwitcher = () => setLangSwitcherOpened((prev) => !prev);

  const [changeLanguage] = useChangeUserLanguage(toggleLanguageSwitcher);
  const { data: MeData } = useQuery<MeWithTutorialQuery>(meWithTutorialQuery, {
    fetchPolicy: 'cache-first',
  });
  const { data: languagesData } = useQuery<LanguagesQuery>(languagesQuery);

  const handleLanguageChange = (languageCode: string) => () => {
    if (MeData?.me === null) {
      i18n.changeLanguage(languageCode);
    } else {
      changeLanguage({
        variables: {
          input: languageCode,
        },
      });
    }
  };

  const currentLanguage = getCurrentLanguage(languagesData, MeData);
  const showDropdown =
    currentLanguage && languagesData && languagesData?.languages?.length > 1;
  return (
    <>
      {showDropdown && (
        <DropDown
          opened={langSwitcherOpened}
          backgroundColor={colors.darkGray}
          narrow={true}
          toggleOptions={toggleLanguageSwitcher}
          toggleButtonContent={
            <ContentContainer>
              {currentLanguage?.name}
              <StyledArrow up={langSwitcherOpened} />
            </ContentContainer>
          }
        >
          {languagesData?.languages ? (
            languagesData.languages
              .filter((lang) => lang.code !== currentLanguage?.code)
              .map(({ name, code }) => (
                <SwitchItem key={code} onClick={handleLanguageChange(code)}>
                  {name}
                </SwitchItem>
              ))
          ) : (
            <SwitchItem>Error</SwitchItem>
          )}
        </DropDown>
      )}
    </>
  );
};

export default LanguageSwitch;

const getCurrentLanguage = (
  languagesData: LanguagesQuery | undefined,
  MeData: Me | undefined,
) => {
  const findLanguage = (langCode: string) =>
    languagesData?.languages.find(
      (language) => language.code.toLowerCase() === langCode.toLowerCase(),
    );

  let currentLanguage;
  const detectedLang = i18next.languages[0];
  if (MeData?.me?.language) {
    currentLanguage = findLanguage(MeData?.me?.language);
  }
  if (!currentLanguage) {
    currentLanguage = findLanguage(detectedLang);
  }
  if (!currentLanguage) {
    currentLanguage = findLanguage(
      process.env.REACT_APP_LOCIZE_LANGUAGE || 'cs',
    );
  }
  // Should be falsy when REACT_APP_LOCIZE_SUPPORTED_LANGUAGES is correct
  if (currentLanguage && currentLanguage.code !== detectedLang) {
    i18n.changeLanguage(currentLanguage.code);
  }
  return currentLanguage;
};

export const changeUserLanguageMutation = gql`
  mutation ChangeUserLanguage($input: String!) {
    changeUserLanguage(languageCode: $input) {
      id
      language
    }
  }
`;

const SwitchItem = styled.button`
  border: none;
  outline: none;
  background-color: transparent;
  padding: 0;
  color: #fff;
  &:hover {
    color: ${colors.lobbyOrange};
  }
`;

const ContentContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  backgroundColor: colors.darkGray,
  minHeight: 45,
  color: colors.white,
  fontSize: '1em',
  letterSpacing: 0,
  padding: '.5em 1em',
  height: '100%',
});
